/**
 * WebhookDetailModal — Modal overlay showing webhook delivery details.
 *
 * Opens when user clicks "View Details" on a webhook event.
 * Shows: webhook URL, status code, error code, request body, response body.
 * If FI data was offloaded to S3 (has_s3_data=true), fetches the full body
 * on-demand via /webhookFullBody API when user opens the modal.
 */

import React, { useState, useEffect } from 'react';
import { X, Copy, Check, AlertTriangle, Loader } from 'lucide-react';
import { httpClient } from '../../../services/http-client.service';
import type { TimelineEvent } from '../../../types/timeline';

interface WebhookDetailModalProps {
  event: TimelineEvent;
  onClose: () => void;
}

function formatJson(str: string | null | undefined): string {
  if (!str) return '—';
  try {
    const parsed = typeof str === 'string' ? JSON.parse(str) : str;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return str;
  }
}

const CopyableBlock: React.FC<{ label: string; content: string; loading?: boolean }> = ({ label, content, loading }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{label}</span>
        {!loading && (
          <button
            onClick={handleCopy}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#6b7280',
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      {loading ? (
        <div style={{
          backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          fontSize: '12px', color: '#6b7280',
        }}>
          <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
          Fetching data from storage...
        </div>
      ) : (
        <pre style={{
          backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '12px', fontSize: '12px', color: '#374151', overflowX: 'auto',
          maxHeight: '240px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          margin: 0, lineHeight: 1.6,
        }}>
          {content || '—'}
        </pre>
      )}
    </div>
  );
};

export const WebhookDetailModal: React.FC<WebhookDetailModalProps> = ({ event, onClose }) => {
  const meta = event.metadata || {};
  const webhookUrl = meta.webhook_url as string | undefined;
  const requestBody = meta.webhook_request_body as string | undefined;
  const responseBody = meta.webhook_response_body as string | undefined;
  const statusCode = meta.response_status as number | undefined;
  const errorCode = meta.webhook_error_code as string | undefined;
  const eventName = (meta.webhook_event_type as string) || 'Webhook';
  const hasS3Data = meta.has_s3_data as boolean | undefined;

  // Extract webhook DB id from event.id format: "webhook_{event_type}_{id}"
  const webhookId = event.id?.split('_').pop();

  const [fullBody, setFullBody] = useState<string | null>(null);
  const [s3Loading, setS3Loading] = useState(false);
  const [s3Error, setS3Error] = useState<string | null>(null);

  // Fetch full body from S3 on mount if data was offloaded
  useEffect(() => {
    if (!hasS3Data || !webhookId) return;

    setS3Loading(true);
    httpClient.post<{ status: string; data: any }>('webhookFullBody', { webhook_id: parseInt(webhookId) })
      .then(response => {
        const result = response.data?.data || response.data;
        if (result?.fi_data) {
          setFullBody(JSON.stringify(result.fi_data, null, 2));
        } else if (result?.status === 'purged') {
          setFullBody('FI data has been purged per retention policy.');
        }
      })
      .catch(err => {
        setS3Error('Failed to fetch data: ' + (err?.message || 'Unknown error'));
      })
      .finally(() => setS3Loading(false));
  }, [hasS3Data, webhookId]);

  const hasError = errorCode || (statusCode !== undefined && (statusCode === 0 || statusCode >= 400));

  // Use fetched full body if available, otherwise use the request body from timeline
  const displayRequestBody = fullBody || requestBody;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9998,
        }}
      />
      {/* Modal panel */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '600px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
        zIndex: 9999, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
          position: 'sticky', top: 0, backgroundColor: '#fff', borderRadius: '12px 12px 0 0',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>
              Webhook Delivery Details
            </h3>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              {eventName} — {typeof event.timestamp === 'string' ? event.timestamp : event.timestamp?.toLocaleString?.() || ''}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              borderRadius: '6px', display: 'flex',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X style={{ width: 20, height: 20, color: '#6b7280' }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* URL */}
          {webhookUrl && (
            <div style={{ marginBottom: '16px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>URL</span>
              <p style={{
                fontSize: '13px', color: '#1f2937', marginTop: '4px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                wordBreak: 'break-all', margin: '4px 0 0 0',
                backgroundColor: '#f9fafb', padding: '8px 10px', borderRadius: '6px',
                border: '1px solid #e5e7eb',
              }}>
                {webhookUrl}
              </p>
            </div>
          )}

          {/* Status row */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {statusCode !== undefined && statusCode > 0 && (
              <div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Status Code</span>
                <p style={{
                  fontSize: '20px', fontWeight: 700, margin: '4px 0 0 0',
                  color: statusCode >= 200 && statusCode < 300 ? '#16a34a' : '#dc2626',
                }}>
                  {statusCode}
                </p>
              </div>
            )}
            {errorCode && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <AlertTriangle style={{ width: 16, height: 16, color: '#f59e0b', marginTop: '20px' }} />
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Error Code</span>
                  <p style={{ fontSize: '20px', fontWeight: 700, color: '#d97706', margin: '4px 0 0 0' }}>
                    {errorCode}
                  </p>
                </div>
              </div>
            )}
            {!statusCode && !errorCode && (
              <div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Status</span>
                <p style={{ fontSize: '14px', fontWeight: 600, color: hasError ? '#dc2626' : '#16a34a', margin: '4px 0 0 0' }}>
                  {event.status?.label || '—'}
                </p>
              </div>
            )}
          </div>

          {/* S3 fetch error */}
          {s3Error && (
            <div style={{
              marginBottom: '16px', padding: '8px 12px', borderRadius: '6px',
              backgroundColor: '#fef2f2', border: '1px solid #fecaca', fontSize: '12px', color: '#b91c1c',
            }}>
              {s3Error}
            </div>
          )}

          {/* Request Body */}
          <CopyableBlock
            label="Request Body"
            content={formatJson(displayRequestBody)}
            loading={s3Loading}
          />

          {/* Response Body */}
          <CopyableBlock
            label="Response Body"
            content={responseBody ? formatJson(responseBody) : (errorCode ? 'No response received (network error)' : '—')}
          />
        </div>
      </div>

      {/* Inline keyframe for spinner */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
};
